import type { ConsentChoice } from "@/lib/consent/signals";

export const CONSENT_COOKIE = "tkf_consent";
export const GEO_COOKIE = "tkf_geo";
/** Bump to re-ask everyone (e.g. when categories change). */
export const CONSENT_VERSION = 1;
/** Six months (182 days); after that the banner asks again. */
export const CONSENT_MAX_AGE_SECONDS = 182 * 24 * 60 * 60;

export function readCookie(name: string): string | undefined {
  try {
    const prefix = `${name}=`;
    return document.cookie
      .split("; ")
      .find((part) => part.startsWith(prefix))
      ?.slice(prefix.length);
  } catch {
    return undefined;
  }
}

/** Decode a raw `tkf_consent` value; null when missing, invalid, another version, or expired. */
export function parseConsent(
  raw: string | undefined,
  now: number = Date.now(),
): ConsentChoice | null {
  if (!raw) return null;
  try {
    const record = JSON.parse(decodeURIComponent(raw));
    if (
      !record ||
      record.v !== CONSENT_VERSION ||
      typeof record.analytics !== "boolean" ||
      typeof record.marketing !== "boolean" ||
      typeof record.ts !== "number" ||
      now - record.ts > CONSENT_MAX_AGE_SECONDS * 1000
    ) {
      return null;
    }
    return { analytics: record.analytics, marketing: record.marketing };
  } catch {
    return null;
  }
}

export function readConsent(now: number = Date.now()): ConsentChoice | null {
  return parseConsent(readCookie(CONSENT_COOKIE), now);
}

/** Persist the choice. Silently does nothing when cookies are blocked. */
export function writeConsent(
  choice: ConsentChoice,
  now: number = Date.now(),
): void {
  try {
    const value = encodeURIComponent(
      JSON.stringify({ v: CONSENT_VERSION, ...choice, ts: now }),
    );
    const secure = window.location.protocol === "https:" ? "; Secure" : "";
    // biome-ignore lint/suspicious/noDocumentCookie: must be readable by the inline bootstrap script before React loads
    document.cookie = `${CONSENT_COOKIE}=${value}; Path=/; Max-Age=${CONSENT_MAX_AGE_SECONDS}; SameSite=Lax${secure}`;
  } catch {
    // Cookies blocked: consent still applies for this page view.
  }
}

/** Visitor country from the middleware (`cf-ipcountry`), if known. */
export function readGeo(): string | undefined {
  return readCookie(GEO_COOKIE) || undefined;
}
